import { createAppReloadSafetyGuard } from './appUpdateSafety';

interface Navigation {
  key: string;
  safe: boolean;
  url: string;
}

interface BuildVersion {
  buildId: string;
  version: string;
}

interface Options {
  buildId: string;
  getNavigation: () => Navigation;
  subscribe: (listener: () => void) => () => void;
  replace?: (url: string) => void;
}

const ATTEMPTED_BUILDS_KEY = 'app-update-attempted-builds';
const CHECK_INTERVAL = 2 * 60 * 1000;

export function startAppUpdateMonitor(options: Options) {
  const safety = createAppReloadSafetyGuard();
  const replace = options.replace || ((url: string) => window.location.replace(url));
  let stopped = false;
  let sequence = 0;
  let navigation = options.getNavigation();
  let pendingCheck: Promise<BuildVersion | null> | undefined;
  let navigationTimer: number | undefined;

  const fetchFresh = async (url: string) => window.fetch(url, {
    cache: 'no-store',
    credentials: 'same-origin',
  });

  const checkVersion = (): Promise<BuildVersion | null> => {
    if (pendingCheck) return pendingCheck;
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    pendingCheck = fetchFresh(`${base}/version.json?_check=${Date.now()}`)
      .then(response => response.ok ? response.json() : null)
      .then((manifest: BuildVersion | null) => {
        return manifest && typeof manifest.buildId === 'string' && typeof manifest.version === 'string'
          ? manifest : null;
      })
      .catch(() => null)
      .then(manifest => {
        pendingCheck = undefined;
        return manifest;
      });
    return pendingCheck;
  };

  const canReload = (expectedSequence: number) => !stopped
    && sequence === expectedSequence
    && options.getNavigation().safe
    && safety.canReload();

  const reloadOnNavigation = async (expectedSequence: number, sourceWasSafe: boolean) => {
    const manifest = await checkVersion();
    if (!sourceWasSafe || !manifest || manifest.buildId === options.buildId || !canReload(expectedSequence)) return;

    try {
      const attempted: string[] = JSON.parse(sessionStorage.getItem(ATTEMPTED_BUILDS_KEY) || '[]');
      if (!Array.isArray(attempted) || attempted.indexOf(manifest.buildId) !== -1) return;

      const destination = new URL(options.getNavigation().url);
      destination.searchParams.set('_app_build', manifest.buildId);
      const response = await fetchFresh(destination.href);
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (html.querySelector('meta[name="app-build-id"]')?.getAttribute('content') !== manifest.buildId) return;
      if (!canReload(expectedSequence)) return;

      sessionStorage.setItem(ATTEMPTED_BUILDS_KEY, JSON.stringify([...attempted.slice(-19), manifest.buildId]));
      replace(destination.href);
    } catch {
      // Network failures, partial deployments or storage restrictions defer the update.
    }
  };

  const unsubscribe = options.subscribe(() => {
    const next = options.getNavigation();
    if (next.key === navigation.key) return;
    navigation = next;
    const expectedSequence = ++sequence;
    const sourceWasSafe = safety.canReload();
    if (navigationTimer !== undefined) window.clearTimeout(navigationTimer);
    navigationTimer = window.setTimeout(() => {
      void reloadOnNavigation(expectedSequence, sourceWasSafe);
    }, 0);
  });

  const checkInBackground = () => { void checkVersion(); };
  const interval = window.setInterval(checkInBackground, CHECK_INTERVAL);
  window.addEventListener('focus', checkInBackground);
  checkInBackground();

  return () => {
    stopped = true;
    unsubscribe();
    window.clearInterval(interval);
    if (navigationTimer !== undefined) window.clearTimeout(navigationTimer);
    window.removeEventListener('focus', checkInBackground);
    safety.dispose();
  };
}
